import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { AwsAuthService } from '@nx/auth';
import { AuthProcessService } from '../../../common/services/auth-process.service';
import * as SignInValidators from '../../validators/sign-in.validators';
import { SignInStore } from '../../interfaces/sign-in-store.interface';
import { signInPasswordState } from '../../routing/route-locales/sign-in-routes';
import { AuthProcessProvider } from '../../../providers/auth-process-provider';
import { SignInService } from '../../services/sign-in.service';
import { ForgottenPasswordCms } from '../../interfaces/forgotten-password-cms.interface';
import { forbiddenInputsValidator } from '@nx/mono-common';

@Component({
  selector: 'lmw-auth-sign-in-forgotten-password',
  templateUrl: './sign-in-forgotten-password.component.html',
})
export class SignInForgottenPasswordComponent
  implements OnInit, OnDestroy, AfterViewInit {

  private readonly halfSecondDelay: number = 500;

  public resetCodeForm: FormGroup;
  public newPasswordForm: FormGroup;
  public showPassword = false;
  public showEnterResetCodeSlider = false;
  public showFeedbackCallout = '';
  public showSendingLoader = false;
  public showSignInLoader: Observable<boolean> = this.signInService.selectSignInPending();
  public canNavigate = false;
  public slideTopPosition: number;
  public signInEmail = false;
  public signInPasswordStage = `../${signInPasswordState.path}`;
  public openHelpPopup: string;
  public _resetCodeInputWrapper: ElementRef;
  public _passwordInputWrapper: ElementRef;
  public forgottenPasswordCms: ForgottenPasswordCms;

  private inputHasFocus = false;
  private resetCodeFormSubscription: Subscription;
  private newPasswordFormSubscription: Subscription;
  private resetCodeEntered: string;
  private passwordEntered: string;
  private signInAttribute: string;

  @ViewChild('resetCodeInput') resetCodeInput: ElementRef;
  @ViewChild('enterResetCodeSlider') enterResetCodeSlider: ElementRef;
  @ViewChild('passwordForm') passwordForm: ElementRef;
  @ViewChild('passwordInputWrapper') passwordInputWrapper: ElementRef;
  @ViewChild('passwordInput') passwordInput: ElementRef;
  @ViewChild('resetCodeSliderForm') resetCodeSliderForm: ElementRef;
  @ViewChild('resetCodeInputWrapper') resetCodeInputWrapper: ElementRef;
  @ViewChild('footer') footer: ElementRef;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (!this.showEnterResetCodeSlider) {
      this.preventSliderFromPeakingOutOnResize();
    } else {
      this.setTopOfSliderPosition();
    }
  }

  constructor(
    public authProcessProvider: AuthProcessProvider,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private awsAuthService: AwsAuthService,
    private authProcessService: AuthProcessService,
    private signInService: SignInService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.forgottenPasswordCms = this.route.snapshot.data.cmsData;
    this.createNewPasswordForm();
    this.createResetCodeForm();
    this.subscribeToResetCodeFormValueChanges();
    this.subscribeToPasswordFormValueChanges();
    this.selectUsernameEmailOrPhoneNumberData(
      this.route.snapshot.data.signInDetails
    );
  }

  ngAfterViewInit(): void {
    this._resetCodeInputWrapper = this.resetCodeInputWrapper;
    this._passwordInputWrapper = this.passwordInputWrapper;
    this.setTopOfSliderPosition();
    this.changeDetector.detectChanges();
    setTimeout(() => {
      this.showEnterResetCodeSlider = true;
    }, this.halfSecondDelay);
  }

  public doneButtonClicked(buttonName) {
    if (buttonName === 'resetCode') {
      this.resetCodeEntered = this.resetCodeForm.value.resetCode;
      if (this.openHelpPopup === 'resetCode') {
        this.closeTheHelpPopup();
      }
      this.closeResetCodeSlider();
    } else {
      this.passwordEntered = this.newPasswordForm.value.newPassword;
      this.canNavigate = true;
    }
  }

  public inputBlurred() {
    this.inputHasFocus = false;
  }

  public onPasswordInputFocus() {
    this.inputHasFocus = true;
  }

  public showPasswordToggle() {
    this.showPassword = !this.showPassword;
    if (this.inputHasFocus) {
      this.focusInputOnPasswordInput();
    }
  }

  public openTheHelpPopup(calledFrom) {
    this.openHelpPopup = calledFrom;
  }

  public closeTheHelpPopup() {
    this.openHelpPopup = '';
  }

  public sendNewCode() {
    this.showSendingLoader = true;
    this.awsAuthService
      .sendResetPasswordCode(this.signInAttribute, { app: "lmw", appVersion: this.authProcessProvider.appVersion, context: 'forgottenPasswordResend' })
      .subscribe(() => {
        setTimeout(() => {
          this.showSendingLoader = false;
          this.showCorrectFeedbackCallout('resetCode');
        }, this.halfSecondDelay * 2);
      },
      (error) => {
        if (error.message === 'LimitExceededException') {
          this.showCorrectFeedbackCallout('tooManyCodeRequests');
        }
      });
  }

  public newPasswordButtonClicked() {
    this.canNavigate = false;
    this.checkCodeAndNewPassword();
  }

  private createNewPasswordForm() {
    const forbiddenPasswords = [this.forgottenPasswordCms?.data?.attributes?.newPassword?.help.passwordExample];

    this.newPasswordForm = this.formBuilder.group({
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(12),
        Validators.maxLength(50),
        forbiddenInputsValidator(forbiddenPasswords)
      ]),
    });
  }

  private createResetCodeForm() {
    this.resetCodeForm = this.formBuilder.group({
      resetCode: new FormControl('', [
        Validators.required,
        SignInValidators.resetCodeValidator,
      ]),
    });
  }

  private selectUsernameEmailOrPhoneNumberData(signInStore: SignInStore) {
    if (signInStore.email !== '') {
      this.signInEmail = true;
      this.signInAttribute = signInStore.email;
    } else if (signInStore.phoneNumber !== '') {
      this.signInEmail = false;
      this.signInAttribute = signInStore.phoneNumber;
    }
  }

  private preventSliderFromPeakingOutOnResize() {
    const sliderTopOffscreen = -1000;
    this.slideTopPosition = sliderTopOffscreen;
    setTimeout(() => {
      this.setTopOfSliderPosition();
    }, this.halfSecondDelay / 2);
  }

  private setTopOfSliderPosition() {
    const sliderShadowSizeOutsideOfBoxModel = 2;
    this.slideTopPosition =
      (this.enterResetCodeSlider.nativeElement.offsetHeight +
        sliderShadowSizeOutsideOfBoxModel) *
      -1;
  }

  private closeResetCodeSlider() {
    setTimeout(() => {
      this.showEnterResetCodeSlider = false;
    }, this.halfSecondDelay);
    this.checkIfPasswordFieldIsValid();
  }

  private checkIfPasswordFieldIsValid() {
    if (this.newPasswordForm.valid) {
      this.canNavigate = true;
    }
  }

  private focusInputOnPasswordInput() {
    if (this.inputHasFocus) {
      this.authProcessService.setCursorToEndOfInput('new-password');
    }
  }

  private showCorrectFeedbackCallout(callout) {
    this.showFeedbackCallout = callout;
    let delayMultiplier = 6;
    if (callout === 'tooManyCodeRequests' || callout === 'tooManySignInAttempts') {
      delayMultiplier = 20;
    }
    this.hideFeedBackCalloutAfterDelay(delayMultiplier, callout);
  }

  private hideFeedBackCalloutAfterDelay(delayMultiplier, callout) {
    setTimeout(() => {
      this.showFeedbackCallout = '';
      if (callout === 'tooManySignInAttempts') {
        this.showResetCodeSliderAndHelp(false);
      } else {
        this.showSendingLoader = false;
      }
    }, this.halfSecondDelay * delayMultiplier);
  }

  private checkCodeAndNewPassword() {
    this.awsAuthService
      .createNewPassword(
        this.signInAttribute,
        this.resetCodeEntered,
        this.passwordEntered
      )
      .subscribe(
        () => {
          this.showCorrectFeedbackCallout('password');
          setTimeout(() => {
            this.updateStoreWithNewPasswordAndCode();
          }, this.halfSecondDelay * 4);
        },
        (error) => {
          if (error.message === 'CodeMismatchException') {
            this.showResetCodeSliderAndHelp(true);
          }
          if (error.message === 'LimitExceededException') {
            this.showCorrectFeedbackCallout('tooManySignInAttempts');
          }
        }
      );
  }

  private updateStoreWithNewPasswordAndCode() {
    const resetCodeObject: any = { passwordResetCode: this.resetCodeEntered };
    this.signInService.dispatchUpdateSignInStore(
      resetCodeObject,
      this.signInAttribute
    );
    setTimeout(() => {
      this.signUserIn();
    }, this.halfSecondDelay * 2);
  }

  private signUserIn() {
    this.signInService.dispatchSignInUser(
      this.signInAttribute,
      this.passwordEntered
    );
  }

  private showResetCodeSliderAndHelp(openHelp) {
    this.showEnterResetCodeSlider = true;
    setTimeout(() => {
      this.openTheHelpPopup('resetCode');
    }, this.halfSecondDelay * 2);
  }

  private subscribeToResetCodeFormValueChanges() {
    this.resetCodeFormSubscription = this.resetCodeForm.valueChanges.subscribe(
      (resetCode) => {
        if (resetCode) {
          if (
            this.resetCodeEntered !== this.resetCodeForm.get('resetCode').value
          ) {
            this.canNavigate = false;
            this.showEnterResetCodeSlider = true;
          }
        }
      }
    );
  }

  private subscribeToPasswordFormValueChanges() {
    this.newPasswordFormSubscription = this.newPasswordForm.valueChanges.subscribe(
      (newPassword) => {
        if (newPassword) {
          if (
            this.passwordEntered !==
            this.newPasswordForm.get('newPassword').value
          ) {
            this.canNavigate = false;
          }
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.resetCodeFormSubscription.unsubscribe();
    this.newPasswordFormSubscription.unsubscribe();
  }
}
